/**
 * Shopify Dashboard API Functions
 * Frontend service layer for interacting with the Shopify backend APIs
 */

import { makeProxyRequest } from './api'; // Adjust import path as needed

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface MagicLinkRequest {
  product_url: string;
  persona_id: string;
  product_name?: string;
  product_id?: string;
  metadata?: Record<string, any>;
}

export interface MagicLinkResponse {
  magic_link_id: string;
  tracking_url: string;
  original_url: string;
  redirect_url: string;
  product_id: string;
  product_name: string;
  persona_id: string;
  persona_username: string;
  affiliate_link_id: string;
}

export interface MagicLinkDetails extends MagicLinkResponse {
  persona_name: string;
  merchant_domain: string;
  creator_handle: string;
  status: string;
  created_at: string;
  total_clicks: number;
  metadata: Record<string, any>;
}

export interface BatchMagicLinkResult {
  index: number;
  success: boolean;
  magic_link?: Omit<MagicLinkResponse, 'original_url'>;
  error?: string;
  product_url?: string;
}

export interface BatchMagicLinkResponse {
  total_requested: number;
  successful: number;
  failed: number;
  results: BatchMagicLinkResult[];
  errors: BatchMagicLinkResult[];
}

export interface MagicLinkListItem {
  magic_link_id: string;
  persona_id: string;
  persona_username: string;
  product_id: string;
  product_name: string;
  merchant_domain: string;
  tracking_url: string;
  affiliate_link_id: string;
  status: string;
  created_at: string;
  total_clicks: number;
}

export interface MagicLinkListResponse {
  total: number;
  limit: number;
  items: MagicLinkListItem[];
}

export interface ClickLogResponse {
  click_id: string;
  magic_link_id: string;
  product_id: string;
  redirect_url: string;
  logged_at: string;
}

export interface ClickAnalytics {
  total_clicks: number;
  unique_users: number;
  clicks_by_product: Record<string, number>;
  clicks_by_persona: Record<string, number>;
  clicks_by_date: Record<string, number>;
}

export interface ProductAnalytics {
  product_id: string;
  total_clicks: number;
  total_orders: number;
  conversion_rate: number;
  total_revenue: number;
  total_commission: number;
  clicks_last_7_days: number;
  is_trending: boolean;
}

export interface ConversionMetrics {
  period_days: number;
  total_clicks: number;
  total_orders: number;
  conversion_rate_percent: number;
  total_revenue: number;
  total_commission: number;
  average_order_value: number;
  estimated_revenue_per_click: number;
}

export interface TrendingProduct {
  product_id: string;
  total_clicks: number;
  conversion_rate: number;
  is_trending: boolean;
}

export interface TrendingProductsResponse {
  period_days: number;
  trending_products: TrendingProduct[];
}

export interface PersonaAnalytics {
  persona_id: string;
  persona_username: string;
  persona_name: string;
  affiliate_link_id: string;
  creator_handle: string;
  total_magic_links: number;
  total_clicks: number;
  unique_users: number;
  clicks_by_product: Record<string, number>;
  clicks_by_date: Record<string, number>;
  conversion_metrics: ConversionMetrics;
}

export interface SyncOrdersResponse {
  synced_orders: number;
  total_commission: number;
  sync_time: string;
}

export interface DashboardSummary {
  summary: {
    total_magic_links: number;
    total_clicks: number;
    unique_users: number;
    total_personas: number;
  };
  conversion_metrics: ConversionMetrics;
  top_personas: Record<string, number>;
  clicks_by_date: Record<string, number>;
}

// ============================================================================
// Magic Link APIs
// ============================================================================

/**
 * Generate a new Magic Link for a Shopify product
 */
export async function createMagicLink(
  request: MagicLinkRequest
): Promise<MagicLinkResponse> {
  const endpoint = 'api/shopify/magic-link';

  try {
    const response = await makeProxyRequest(endpoint, 'POST', request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error creating Magic Link:', error);
    throw error;
  }
}

/**
 * Generate multiple Magic Links in a single batch request
 */
export async function createMagicLinksBatch(
  products: MagicLinkRequest[]
): Promise<BatchMagicLinkResponse> {
  const endpoint = 'api/shopify/magic-links/batch';

  try {
    const response = await makeProxyRequest(endpoint, 'POST', { products });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error creating batch Magic Links:', error);
    throw error;
  }
}

/**
 * Get details for a specific Magic Link
 */
export async function getMagicLinkDetails(
  magicLinkId: string
): Promise<MagicLinkDetails> {
  const endpoint = `api/shopify/magic-link/${magicLinkId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Magic Link not found');
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching Magic Link details:', error);
    throw error;
  }
}

/**
 * List Magic Links with optional filtering
 */
export async function listMagicLinks(params?: {
  persona_id?: string;
  product_id?: string;
  limit?: number;
}): Promise<MagicLinkListResponse> {
  const queryParams = new URLSearchParams();

  if (params?.persona_id) queryParams.append('persona_id', params.persona_id);
  if (params?.product_id) queryParams.append('product_id', params.product_id);
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const endpoint = `api/shopify/magic-links${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error listing Magic Links:', error);
    throw error;
  }
}

/**
 * Deactivate a Magic Link (preserves click history)
 */
export async function deactivateMagicLink(
  magicLinkId: string
): Promise<{ deactivated: boolean; magic_link_id: string }> {
  const endpoint = `api/shopify/magic-link/${magicLinkId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'DELETE', undefined);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Magic Link not found');
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error deactivating Magic Link:', error);
    throw error;
  }
}

// ============================================================================
// Click Tracking APIs
// ============================================================================

/**
 * Log a click event without redirecting (for client-side tracking)
 */
export async function logClick(
  magicLinkId: string,
  params?: {
    user_id?: string;
    session_id?: string;
  }
): Promise<ClickLogResponse> {
  const queryParams = new URLSearchParams();

  if (params?.user_id) queryParams.append('user_id', params.user_id);
  if (params?.session_id) queryParams.append('session_id', params.session_id);

  const endpoint = `api/shopify/track/${magicLinkId}/log${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'POST', undefined);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Magic Link not found or expired');
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error logging click:', error);
    throw error;
  }
}

/**
 * Get the tracking URL for redirect (used in links)
 */
export function getTrackingUrl(magicLinkId: string): string {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://soopra.ai';
  return `${baseUrl}/track/shopify-${magicLinkId}`;
}

// ============================================================================
// Analytics APIs
// ============================================================================

/**
 * Get aggregated click analytics
 */
export async function getClickAnalytics(params?: {
  magic_link_id?: string;
  persona_id?: string;
  product_id?: string;
  start_date?: string; // ISO format
  end_date?: string; // ISO format
}): Promise<ClickAnalytics> {
  const queryParams = new URLSearchParams();

  if (params?.magic_link_id)
    queryParams.append('magic_link_id', params.magic_link_id);
  if (params?.persona_id) queryParams.append('persona_id', params.persona_id);
  if (params?.product_id) queryParams.append('product_id', params.product_id);
  if (params?.start_date) queryParams.append('start_date', params.start_date);
  if (params?.end_date) queryParams.append('end_date', params.end_date);

  const endpoint = `api/shopify/analytics/clicks${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching click analytics:', error);
    throw error;
  }
}

/**
 * Get detailed analytics for a specific product
 */
export async function getProductAnalytics(
  productId: string
): Promise<ProductAnalytics> {
  const endpoint = `api/shopify/analytics/product/${productId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching product analytics:', error);
    throw error;
  }
}

/**
 * Get conversion metrics (clicks vs orders)
 */
export async function getConversionMetrics(params?: {
  persona_id?: string;
  product_id?: string;
  days?: number;
}): Promise<ConversionMetrics> {
  const queryParams = new URLSearchParams();

  if (params?.persona_id) queryParams.append('persona_id', params.persona_id);
  if (params?.product_id) queryParams.append('product_id', params.product_id);
  if (params?.days) queryParams.append('days', params.days.toString());

  const endpoint = `api/shopify/analytics/conversions${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching conversion metrics:', error);
    throw error;
  }
}

/**
 * Get trending products based on click activity
 */
export async function getTrendingProducts(params?: {
  limit?: number;
  days?: number;
}): Promise<TrendingProductsResponse> {
  const queryParams = new URLSearchParams();

  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.days) queryParams.append('days', params.days.toString());

  const endpoint = `api/shopify/analytics/trending${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching trending products:', error);
    throw error;
  }
}

/**
 * Get analytics for a specific persona/creator
 */
export async function getPersonaAnalytics(
  personaId: string,
  days?: number
): Promise<PersonaAnalytics> {
  const queryParams = new URLSearchParams();
  if (days) queryParams.append('days', days.toString());

  const endpoint = `api/shopify/analytics/persona/${personaId}${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching persona analytics:', error);
    throw error;
  }
}

// ============================================================================
// Dashboard API
// ============================================================================

/**
 * Get high-level dashboard summary
 */
export async function getDashboardSummary(
  days?: number
): Promise<DashboardSummary> {
  const queryParams = new URLSearchParams();
  if (days) queryParams.append('days', days.toString());

  const endpoint = `api/shopify/dashboard${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching dashboard summary:', error);
    throw error;
  }
}

// ============================================================================
// Order Sync API
// ============================================================================

/**
 * Manually trigger order sync from Shopify
 * Note: This typically runs automatically via cron job every 6 hours
 */
export async function syncShopifyOrders(
  sinceDate?: string // ISO format
): Promise<SyncOrdersResponse> {
  const endpoint = 'api/shopify/orders/sync';

  try {
    const response = await makeProxyRequest(endpoint, 'POST', {
      since_date: sinceDate,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error syncing Shopify orders:', error);
    throw error;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format currency value for display
 */
export function formatCurrency(
  value: number,
  currency: string = 'USD'
): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

/**
 * Format large numbers with K/M suffixes
 */
export function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);
}

/**
 * Calculate growth percentage between two values
 */
export function calculateGrowth(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

// Add to shopifyApi.ts

// ============================================================================
// Creator Affiliate Config APIs
// ============================================================================

export interface UpdateCreatorAffiliateRequest {
  persona_id: string;
  affiliate_link_id: string;
  creator_handle?: string;
  commission_rate?: number;
}

export interface CreatorAffiliateResponse {
  success: boolean;
  persona_id: string;
  affiliate_link_id: string;
  creator_handle: string;
  commission_rate: number;
}

export interface CreatorAffiliateConfig {
  persona_id: string;
  userName: string;
  fullName: string;
  has_affiliate_config: boolean;
  shopify_collabs: {
    affiliate_link_id: string;
    creator_handle: string;
    commission_rate: number;
  };
}

export interface CreatorListItem {
  persona_id: string;
  userName: string;
  fullName: string;
  profilePicUrl: string;
  has_affiliate_config: boolean;
  affiliate_link_id: string;
}

export interface CreatorListResponse {
  total: number;
  creators: CreatorListItem[];
}

/**
 * Update a creator's Shopify Collabs affiliate configuration
 * This must be done before generating Magic Links for the creator
 */
export async function updateCreatorAffiliateConfig(
  request: UpdateCreatorAffiliateRequest
): Promise<CreatorAffiliateResponse> {
  const endpoint = 'api/shopify/creators/affiliate-config';

  try {
    const response = await makeProxyRequest(endpoint, 'POST', request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error updating creator affiliate config:', error);
    throw error;
  }
}

/**
 * Get a creator's current Shopify Collabs configuration
 */
export async function getCreatorAffiliateConfig(
  personaId: string
): Promise<CreatorAffiliateConfig> {
  const endpoint = `api/shopify/creators/${personaId}/affiliate-config`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Creator not found');
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching creator affiliate config:', error);
    throw error;
  }
}

/**
 * List all creators with their affiliate configuration status
 */
export async function listCreatorsWithAffiliateStatus(
  limit?: number
): Promise<CreatorListResponse> {
  const queryParams = new URLSearchParams();
  if (limit) queryParams.append('limit', limit.toString());

  const endpoint = `api/shopify/creators${
    queryParams.toString() ? `?${queryParams}` : ''
  }`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error listing creators:', error);
    throw error;
  }
}
